import {
  BookOutlined,
  CheckCircleFilled,
  CheckCircleOutlined,
  DownloadOutlined,
  PlusOutlined,
  WarningFilled,
} from '@ant-design/icons'
import { useOrders } from '@api/useOrders'
import { OrderModalButton } from '@components/_SiteWide/OrderDisplay/OrderModalButton'
import { OrderProducts, ProductPill } from '@components/_SiteWide/OrderDisplay/OrderProducts'
import { OrderStores } from '@components/_SiteWide/OrderDisplay/OrderStores'
import { OrderTerminals } from '@components/_SiteWide/OrderDisplay/OrderTerminals'
import { DispatchStatusTag } from '@components/_SiteWide/StoreDisplay/DispatchStatusTag'
import { BBDTag, GraviButton, Horizontal, MomentDatePicker, Texto, Vertical } from '@gravitate-js/excalibrr'
import { useQueryClient } from '@tanstack/react-query'
import { formatters } from '@utils/intl/dateTimeFormatters'
import { Col, Popconfirm, Popover, Row, Tooltip } from 'antd'
import moment from 'moment'
import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { useOrderMovements } from '@api/useOrderMovements'
import { datePickerValidators } from '@utils/datePickerValidator'

export function ShowOrderModalRenderer({ data, showModal }) {
  return (
    <Row>
      <Col className='vertical-flex-center pl-1' span={12}>
        <OrderModalButton showModal={showModal} orderId={data.id} />
      </Col>
      <Col className='vertical-flex-center pl-1' span={12}>
        <Tooltip title='View BOL' mouseEnterDelay={0.5} placement='bottom'>
          <Link
            to={`/order_movements_page/report?${new URLSearchParams({ order_number: data.number })}`}
            target='_blank'
          >
            <GraviButton shape='circle' className='mt-4' icon={<BookOutlined />} />
          </Link>
        </Tooltip>
      </Col>
    </Row>
  )
}

export function AssignedDriverRenderer({ data }) {
  return (
    <div className='vertical-flex-center'>
      <DispatchStatusTag stackCarrierWithDriver order={data} />
    </div>
  )
}

const attentionReasonLUT = {
  needs_bols: {
    label: 'Needs Delivery Info',
    icon: <BookOutlined className='pr-3' />,
    theme: 'var(--theme-warning)',
  },
  past_delivery: {
    label: 'Past Delivery',
    icon: <WarningFilled className='pr-3' />,
    theme: 'var(--theme-warning)',
  },
  missing_price: {
    label: 'Missing Price',
    icon: <BookOutlined className='pr-3' />,
    theme: 'var(--theme-error)',
  },
  needs_review: {
    label: 'Needs Review',
    icon: <WarningFilled className='pr-3' />,
    theme: 'var(--theme-warning)',
  },
}

const statusLut = {
  scheduled: {
    label: 'Scheduled',
    icon: <DownloadOutlined style={{ transform: 'rotate(-90deg)' }} className='mr-2' />,
    theme: 'var(--theme-color-2)',
  },
  delivered: {
    label: 'Delivered',
    icon: <DownloadOutlined className='mr-2' />,
    theme: 'var(--theme-success)',
  },
  completed: {
    label: 'Completed',
    icon: <CheckCircleFilled className='mr-2' />,
    theme: 'var(--theme-success)',
  },
}

export function MovementStatus({ data, value }) {
  if (data.needs_attention_reason !== null) {
    const reason = attentionReasonLUT[data.needs_attention_reason]
    return (
      <div>
        <BBDTag
          {...{
            error: data.needs_attention_reason == 'missing_price',
            warning: data.needs_attention_reason !== 'missing_price',
          }}
        >
          {reason.icon}
          {reason.label}
        </BBDTag>
      </div>
    )
  }

  if (data.movement_status !== null) {
    const status = statusLut[data.movement_status]
    return (
      <div>
        <BBDTag style={{ color: 'white', backgroundColor: status.theme, border: 0 }}>
          {status.icon}
          {status.label}
        </BBDTag>
      </div>
    )
  }
  return null
}

export function MovementAction({ data, api, handleDeliveryModalOpen }) {
  const { useBypassValidationIssueMutation } = useOrderMovements()
  const mutation = useBypassValidationIssueMutation({
    onSuccess: () => api.redrawRows(),
  })

  if (data.needs_attention_reason === 'needs_review') {
    return (
      <Popconfirm
        placement='topRight'
        title='Are you sure you want to mark this order as reviewed?'
        okText='Yes'
        cancelText='No'
        onConfirm={() => mutation.mutate(data.id)}
      >
        <GraviButton theme2 appearance='outline' buttonText='Mark As Reviewed' icon={<CheckCircleOutlined />} />
      </Popconfirm>
    )
  }
  if (data.movement_status === 'scheduled') {
    return <MarkDeliveredPopoverButton data={data} api={api} />
  }
  if (data.movement_status === 'delivered') {
    return (
      <GraviButton
        theme2
        appearance='outline'
        buttonText='Add Delivery Info'
        icon={<PlusOutlined />}
        onClick={() => handleDeliveryModalOpen(data)}
      />
    )
  }
  if (data.movement_status === 'completed' || data.needs_attention_reason === 'missing_price') {
    return (
      <GraviButton
        theme2
        appearance='outline'
        buttonText='View Details'
        onClick={() => handleDeliveryModalOpen(data)}
      />
    )
  }

  return null
}

export function StoresRenderer({ data }) {
  return <OrderStores order={data} />
}

export function TerminalsRenderer({ data }) {
  return <OrderTerminals order={data} showShortName={false} />
}

export function ProductsRenderer({ data }) {
  return <OrderProducts order={data} />
}

export function WindowTimeRenderer({ data, value }) {
  return (
    <Texto
      appearance={data.needs_attention_reason === 'Past Delivery' ? 'warning' : 'secondary'}
      category='p2'
      weight={600}
      textTransform='uppercase'
    >
      {value}
    </Texto>
  )
}

function MarkDeliveredPopoverButton({ data, api }) {
  const [showingPopover, setShowingPopover] = useState()
  const [selectedDate, setSelectedDate] = useState(moment())
  const [deliveryDateTime, setDeliveryDateTime] = useState(moment())
  const queryClient = useQueryClient()
  const { markAsDeliveredMutation } = useOrders()
  const { mutateAsync: markAsDelivered } = markAsDeliveredMutation

  const hidePopover = () => {
    setShowingPopover(false)
  }

  return (
    <Popover
      placement='left'
      content={
        <div>
          <MomentDatePicker
            showTime
            showSecond={false}
            value={deliveryDateTime}
            onChange={setDeliveryDateTime}
            disabledDate={datePickerValidators.DATE_PAST_ONLY}
            disabledTime={(date) => {
              if (date && moment(date).isSame(moment(), 'day')) {
                return {
                  disabledHours: datePickerValidators.HOURS_PAST_ONLY,
                  disabledMinutes: datePickerValidators.MINUTES_PAST_ONLY,
                }
              }
              return {
                disabledHours: () => [],
                disabledMinutes: () => [],
              }
            }}
            showConfirm={false}
            onSelect={(date) => {
              setSelectedDate(date)
            }}
          />
          <div className='mt-3 special-pay-reject'>
            <GraviButton buttonText='Cancel' appearance='outline' onClick={hidePopover} />
            <GraviButton
              theme2
              buttonText='Mark Delivered'
              disabled={!deliveryDateTime}
              className='reject-special-pay ml-2'
              onClick={() => {
                markAsDelivered({ order_ids: [data.id], actual_end_date: deliveryDateTime.toISOString() }).then(() => {
                  queryClient.invalidateQueries({ queryKey: ['siteViewOrders'] }).then(() => api.redrawRows())
                })
                hidePopover()
              }}
            />
          </div>
        </div>
      }
      title='Please enter a delivery time for the order.'
      trigger='click'
      visible={showingPopover}
    >
      <GraviButton
        theme2
        appearance='outline'
        buttonText='Mark Delivered'
        className='mr-2'
        onClick={() => {
          setShowingPopover(true)
        }}
      />
    </Popover>
  )
}
